/**
 * <pg-sticky-atc> — Ürün sayfası sticky satın alma çubuğu
 *
 * - Ana "Sepete Ekle" alanı ekrandan yukarı çıkınca görünür (IntersectionObserver, scroll listener yok).
 * - Varyant değişiminde (tema olayı: `variant:updated`) fiyat ve stok durumu güncellenir.
 * - "Sepete Ekle" ana formdaki [name="add"] butonunu tetikler (tema sepet akışı, çekmece vb. korunur).
 * - "Hemen Satın Al" yalnızca Shopify dinamik ödeme butonu sayfada varsa görünür ve onu tetikler.
 * - Tema editöründe section yeniden yüklendiğinde disconnectedCallback ile tüm dinleyiciler temizlenir.
 */
import { formatMoney } from '@theme/utilities';

class PgStickyAtc extends HTMLElement {
  #observer = null;
  #mutation = null;
  #abort = null;
  #anchorVisible = true;
  #anchorAbove = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.section = this.closest('.shopify-section') || document;
    this.form = document.getElementById(this.dataset.form);
    this.anchor = this.section.querySelector('[data-bup-buy-anchor]');
    this.priceEl = this.querySelector('[data-price]');
    this.addBtn = this.querySelector('[data-action="add"]');
    this.buyBtn = this.querySelector('[data-action="buy-now"]');

    if (!this.form || !this.anchor) return;

    this.hidden = false; // görünürlük CSS sınıfı ile yönetilir

    this.addBtn?.addEventListener('click', () => this.#clickMain('[name="add"]'), { signal });
    this.buyBtn?.addEventListener('click', () => this.#clickDynamicCheckout(), { signal });
    this.section.addEventListener('variant:updated', (e) => this.#onVariant(e.detail?.variant), { signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#anchorVisible = entry.isIntersecting;
      this.#anchorAbove = entry.boundingClientRect.bottom < 0;
      this.#toggle();
    });
    this.#observer.observe(this.anchor);

    // Dinamik ödeme butonu Shopify tarafından asenkron basılır
    this.#syncBuyNow();
    const wrap = this.section.querySelector('[data-pg-dynamic-checkout]');
    if (wrap) {
      this.#mutation = new MutationObserver(() => this.#syncBuyNow());
      this.#mutation.observe(wrap, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#mutation?.disconnect();
    document.documentElement.style.removeProperty('--pg-sticky-atc-h');
  }

  #toggle() {
    const show = !this.#anchorVisible && this.#anchorAbove;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', String(!show));
    this.toggleAttribute('inert', !show);
    // Diğer sabit elemanlar (side-offer tetikleyici vb.) bu değişkene göre yukarı kayabilir
    document.documentElement.style.setProperty('--pg-sticky-atc-h', show ? `${this.offsetHeight}px` : '0px');
  }

  #dynamicButton() {
    return this.section.querySelector('[data-pg-dynamic-checkout] .shopify-payment-button__button');
  }

  #syncBuyNow() {
    if (!this.buyBtn) return;
    const btn = this.#dynamicButton();
    this.buyBtn.hidden = !btn;
    if (btn) this.buyBtn.disabled = btn.disabled || this.addBtn?.disabled;
  }

  #clickMain(selector) {
    const btn = this.form.querySelector(selector);
    if (btn && !btn.disabled) btn.click();
    else this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' }); // seçim eksikse kullanıcıyı forma götür
  }

  #clickDynamicCheckout() {
    const btn = this.#dynamicButton();
    if (btn && !btn.disabled) btn.click();
    else this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #onVariant(variant) {
    const available = Boolean(variant && variant.available);
    if (this.priceEl && variant) this.priceEl.textContent = formatMoney(variant.price);
    if (this.addBtn) {
      this.addBtn.disabled = !available;
      const extras = window.themeHDN?.extras || {};
      this.addBtn.textContent = !variant ? extras.unavailable : available ? extras.addToCart : extras.soldOut;
    }
    this.#syncBuyNow();
  }
}

if (!customElements.get('pg-sticky-atc')) customElements.define('pg-sticky-atc', PgStickyAtc);
